package org.gstechnovos.jexonplastech.sections.about.sections

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextTransform
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.gstechnovos.jexonplastech.sections.about.styles.StoryContainerStyle
import org.gstechnovos.jexonplastech.sections.about.styles.TimelineDotStyle
import org.gstechnovos.jexonplastech.sections.about.styles.TimelineItemStyle
import org.gstechnovos.jexonplastech.theme.toSitePalette
import org.gstechnovos.jexonplastech.util.*
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

// ==========================================
// CMS DATA MODEL
// ==========================================
/**
 *
 * Text:
 *
 * Overline: Renders at 14px in uppercase. Limit to 2–3 words.
 *
 * Headline: Renders at 40px on desktop and 32px on mobile. Limit to 4–7 words.
 *
 * Narrative Paragraph: Renders at 18px. Limit to 1 solid paragraph (approx. 40–60 words).
 * If this is too long, the left column will stretch way past the timeline on the right.
 *
 * Timeline Year: Renders at 16px. Keep this very short (e.g., "2010" or "- 2016").
 *
 * Timeline Title: Renders at 20px. Limit to 3–5 words.
 * Timeline Description: Renders at 16px. Limit to 1–2 sentences.
 * 
 */
data class TimelineMilestone(
    val year: String, val title: String, val description: String
)

data class AboutStoryData(
    val overline: String, val headline: String, val narrative: String, val milestones: List<TimelineMilestone>
)

val defaultAboutStoryData = AboutStoryData(
    overline = "OUR HERITAGE",
    headline = "A Legacy of Quality Manufacturing",
    narrative = "Founded with a vision to revolutionize durable plastic homeware, Jexon Plastech has grown from a humble facility in Rajkot into a premier B2B manufacturing hub. We believe that everyday utility products shouldn't compromise on quality or safety. Today, we supply hundreds of vendors and distributors worldwide with products engineered to last.",
    milestones = listOf(
        TimelineMilestone(
            year = " - 2010",
            title = "Inception in Rajkot",
            description = "Started operations with a single injection molding machine, focusing exclusively on high-durability cleaning brushes."
        ), TimelineMilestone(
            year = "- 2016",
            title = "Facility Expansion",
            description = "Opened a state-of-the-art 50,000 sq. ft. manufacturing plant, expanding our catalog to over 50 unique homeware items."
        ), TimelineMilestone(
            year = "- 2023",
            title = "Global B2B Distribution",
            description = "Achieved major milestones in vendor acquisition, exporting our BPA-free plastic goods to international markets."
        )
    )
)

// ==========================================
// COMPONENT
// ==========================================

@Composable
fun AboutStorySection(data: AboutStoryData = defaultAboutStoryData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()

    // Track scroll visibility for our slide-in animations
    val (isVisible, visibilityRef) = rememberScrollVisibility(threshold = 0.2)

    Box(
        modifier = StoryContainerStyle.toModifier().padding(
            topBottom = if (isDesktop) 100.px else 60.px, leftRight = 5.percent
        ), contentAlignment = Alignment.Center
    ) {
        // Safe Div wrapper to center content and hold the IntersectionObserver
        Div(attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex).justifyContent(JustifyContent.Center).toAttrs {
                ref { element ->
                    visibilityRef(element)
                    onDispose {}
                }
            }) {

            SimpleGrid(
                numColumns(1, lg = 2),
                modifier = Modifier.fillMaxWidth().maxWidth(1200.px).gap(if (isDesktop) 80.px else 40.px)
            ) {
                // --- LEFT COLUMN: Narrative Text (Slides in from left) ---
                Column(
                    modifier = Modifier.fillMaxWidth().animateSlideInLeft(isVisible, delayMs = 0),
                    verticalArrangement = Arrangement.Center
                ) {
                    SpanText(
                        text = data.overline,
                        modifier = Modifier.color(palette.brand.primaryBlue).fontSize(14.px).fontWeight(FontWeight.Bold)
                            .letterSpacing(1.5.px).textTransform(TextTransform.Uppercase).margin(bottom = 12.px)
                    )

                    SpanText(
                        text = data.headline,
                        modifier = Modifier.color(palette.custom.darkText).fontSize(if (isDesktop) 40.px else 32.px)
                            .fontWeight(FontWeight.Bold).lineHeight(1.2).margin(bottom = 24.px)
                    )

                    SpanText(
                        text = data.narrative,
                        modifier = Modifier.color(palette.custom.darkText.toRgb().copyf(alpha = 0.8f)).fontSize(18.px)
                            .lineHeight(1.6)
                    )
                }

                // --- RIGHT COLUMN: Timeline (Slides in from right) ---
                Column(
                    modifier = Modifier.fillMaxWidth().animateSlideInRight(isVisible, delayMs = 200),
                    verticalArrangement = Arrangement.Center
                ) {
                    data.milestones.forEach { milestone ->
                        TimelineNode(milestone)
                    }
                }
            }
        }
    }
}

@Composable
private fun TimelineNode(milestone: TimelineMilestone) {
    val palette = ColorMode.current.toSitePalette()

    Box(modifier = TimelineItemStyle.toModifier()) {

        Column(modifier = Modifier.fillMaxWidth().padding(top = (-2).px)) {
            // Year Tag
            SpanText(
                text = milestone.year,
                modifier = Modifier.color(palette.brand.primaryBlue).fontSize(16.px).fontWeight(FontWeight.Bold)
                    .margin(bottom = 4.px)
            )

            // Title
            SpanText(
                text = milestone.title,
                modifier = Modifier.color(palette.custom.darkText).fontSize(20.px).fontWeight(FontWeight.Bold)
                    .margin(bottom = 8.px)
            )

            // Description
            SpanText(
                text = milestone.description,
                modifier = Modifier.color(palette.custom.darkText.toRgb().copyf(alpha = 0.7f)).fontSize(16.px)
                    .lineHeight(1.5)
            )
        }
    }
}